import { Helmet } from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb, { getBreadcrumbJsonLD } from '@/components/feature/Breadcrumb';
import { getFormUrl } from '@/utils/getFormUrl';
import { useState, useEffect } from 'react';

export default function AboutPage() {
  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '关于我们' },
  ];

  const breadcrumbSchema = getBreadcrumbJsonLD(
    breadcrumbItems,
    'https://www.hanmanzhijia.com'
  );

  const [contactFormUrl, setContactFormUrl] = useState('');
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    getFormUrl('联系我们 - 韩漫之家').then((url) => {
      setContactFormUrl(url);
    });
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError('');
    setFormSuccess('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypotField = form.querySelector<HTMLInputElement>('[name="phone_alt"]');
    if (honeypotField && honeypotField.value.trim() !== '') {
      setFormSuccess('提交成功！我们会尽快回复您。');
      form.reset();
      return;
    }

    if (!contactFormUrl) {
      setFormError('表单暂不可用，请通过电话或邮件联系我们。');
      return;
    }

    const message = formData.get('message') as string;
    if (message && message.length > 500) {
      setFormError('留言内容不能超过500个字符。');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(contactFormUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.message || responseText;

      if (response.ok && parsed?.code === 'OK') {
        setFormSuccess('提交成功！我们会尽快回复您。');
        form.reset();
      } else if (serverMsg && (typeof serverMsg === 'string') && serverMsg.includes('spam')) {
        setFormSuccess('提交成功！我们会尽快回复您。');
        form.reset();
      } else {
        setFormError(serverMsg || '提交失败，请稍后再试。');
      }
    } catch {
      setFormError('网络错误，请稍后再试。');
    } finally {
      setSubmitting(false);
    }
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'AboutPage',
        name: '关于我们 - 韩漫之家',
        description: '韩漫之家提供漫画免费韩漫服务，最新高清免费韩国漫画大全与热门作品均可在线阅读。',
        url: 'https://www.hanmanzhijia.com/about',
      },
      {
        '@type': 'Organization',
        name: '韩漫之家',
        url: 'https://www.hanmanzhijia.com',
        description: '最新高清免费韩国漫画大全，热门韩漫在线阅读平台',
        contactPoint: {
          '@type': 'ContactPoint',
          telephone: '400-888-6666',
          contactType: 'customer service',
          availableLanguage: ['Chinese'],
        },
        address: {
          '@type': 'PostalAddress',
          streetAddress: '望京SOHO T2',
          addressLocality: '北京',
          addressRegion: '北京',
          addressCountry: 'CN',
        },
      },
      breadcrumbSchema,
      {
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: '韩漫之家的漫画都是免费的吗？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '是的，韩漫之家所有漫画均免费阅读，无需付费或开通会员，我们致力于为广大读者提供优质的免费韩漫阅读服务。',
            },
          },
          {
            '@type': 'Question',
            name: '韩漫之家的漫画多久更新一次？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '韩漫之家与多家韩国漫画平台合作，同步韩国最新连载进度，确保在第一时间为读者呈上最新话。大部分热门漫画每周更新1-2话。',
            },
          },
          {
            '@type': 'Question',
            name: '如何在韩漫之家找到想看的漫画？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '您可以通过首页的分类导航浏览不同类别的漫画，如恋爱、奇幻、动作、悬疑等。也可以在搜索框中输入漫画名称进行查找。',
            },
          },
          {
            '@type': 'Question',
            name: '韩漫之家的漫画可以在手机上阅读吗？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '当然可以！韩漫之家采用响应式设计，完美适配电脑、平板和手机等多种设备，无论您使用什么设备，都能获得优质的阅读体验。',
            },
          },
        ],
      },
    ],
  };

  return (
    <>
      <Helmet
        title="关于我们-韩漫之家_品牌介绍_联系方式"
        description="韩漫之家提供漫画免费韩漫服务，了解我们的品牌故事、平台优势和联系方式。韩漫之家致力于为您提供最新高清免费韩国漫画在线阅读体验。"
        keywords="韩漫之家,关于韩漫之家,免费漫画平台,韩国漫画网站"
        canonicalUrl="https://www.hanmanzhijia.com/about"
        ogTitle="关于我们 - 韩漫之家"
        ogDescription="了解韩漫之家的品牌故事、平台优势和联系方式。"
        ogImage="https://readdy.ai/api/search-image?query=Korean%20manhwa%20reading%20website%20about%20us%20page%20banner%2C%20artistic%20watercolor%20style%2C%20warm%20coral%20and%20cream%20tones%2C%20elegant%20composition&width=1200&height=630&seq=og-about&orientation=landscape"
        schema={aboutSchema}
      />
      <main className="min-h-screen bg-background-50">
        <Navbar />

        <div className="pt-20 pb-8 md:pb-12">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <Breadcrumb items={breadcrumbItems} />

            <section className="mt-6 mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-6 rounded-full bg-primary-500"></div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                  关于我们
                </h1>
              </div>

              <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 md:p-8 mb-8">
                <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">品牌介绍</h2>
                <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                  韩漫之家成立于2024年，是国内领先的<strong>免费韩国漫画在线阅读平台</strong>。我们专注于为中文读者提供最新、最全、最高清的韩国漫画资源，致力于打造华语圈首选的韩漫阅读社区。
                </p>
                <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                  平台汇聚了<strong>最新高清免费韩国漫画大全</strong>，涵盖恋爱、奇幻、动作、悬疑、喜剧、剧情、惊悚、日常等丰富分类。无论是热门连载还是经典完结作品，在韩漫之家都能找到。
                </p>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  我们深知每一位漫画爱好者的需求，因此坚持<strong>完全免费</strong>的阅读模式，无需付费、无需会员，让每一位读者都能畅享韩漫的精彩世界。同时，我们与多家韩国知名漫画平台保持紧密合作，确保漫画资源的正版授权和更新速度。
                </p>
              </div>

              <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 md:p-8 mb-8">
                <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">平台优势</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { icon: 'ri-star-line', title: '海量资源', desc: '收录超过5000部韩国漫画作品，涵盖各种热门分类和题材，持续扩充漫画库。' },
                    { icon: 'ri-image-line', title: '高清画质', desc: '所有漫画均提供高清阅读体验，完美呈现韩国漫画精美的绘画风格和细节。' },
                    { icon: 'ri-timer-flash-line', title: '同步更新', desc: '与韩国官方同步更新，确保读者第一时间看到最新连载内容。' },
                    { icon: 'ri-device-line', title: '多端适配', desc: '完美支持电脑、平板、手机等多种设备，随时随地畅享漫画阅读。' },
                    { icon: 'ri-shield-star-line', title: '正版授权', desc: '与多家韩国漫画平台深度合作，确保所有漫画资源的版权合规性。' },
                    { icon: 'ri-money-cny-circle-line', title: '完全免费', desc: '无需付费，无需会员，所有漫画免费阅读，零门槛享受漫画乐趣。' },
                  ].map((item, idx) => (
                    <div key={idx} className="flex gap-3 p-4 rounded-lg bg-background-100">
                      <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                        <i className={item.icon}></i>
                      </div>
                      <div>
                        <h3 className="text-sm font-bold text-foreground-950 mb-1">{item.title}</h3>
                        <p className="text-xs text-foreground-600 leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 md:p-8 mb-8">
                <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">用户口碑</h2>
                <p className="text-sm text-foreground-700 leading-relaxed mb-6">
                  自上线以来，韩漫之家凭借优质的阅读体验和丰富的漫画资源，赢得了广大漫画爱好者的信赖与好评。我们累计服务超过<strong>500万</strong>漫画读者，日均活跃用户超过<strong>30万</strong>，用户满意度达到<strong>98%</strong>。
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {[
                    { name: '小雨', comment: '一直在韩漫之家看漫画，更新速度快，画质也很好，太赞了！强烈推荐给喜欢韩漫的朋友。' },
                    { name: '阿明', comment: '分类很全面，找漫画很方便。而且完全免费，不用开会员就能看到最新的韩漫，良心平台！' },
                    { name: '小樱', comment: '从学生时代就开始用韩漫之家了，资源丰富，界面简洁好用，是我用过最好的漫画阅读网站。' },
                  ].map((review, idx) => (
                    <div key={idx} className="p-4 rounded-lg bg-accent-50">
                      <div className="flex items-center gap-1 mb-2">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <div key={i} className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-star-fill text-accent-500 text-xs"></i>
                          </div>
                        ))}
                      </div>
                      <p className="text-sm text-foreground-700 leading-relaxed mb-3">&ldquo;{review.comment}&rdquo;</p>
                      <p className="text-xs text-foreground-500 font-medium">— {review.name}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 md:p-8 mb-8">
                <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">联系方式</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                  <div className="flex items-center gap-3 p-4 rounded-lg bg-background-100">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">电子邮箱</p>
                      <p className="text-sm font-medium text-foreground-950">contact@hanmanzhijia.com</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-4 rounded-lg bg-background-100">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                      <i className="ri-phone-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">客服电话</p>
                      <p className="text-sm font-medium text-foreground-950">400-888-6666</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-4 rounded-lg bg-background-100">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                      <i className="ri-wechat-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">微信公众号</p>
                      <p className="text-sm font-medium text-foreground-950">韩漫之家</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-4 rounded-lg bg-background-100">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                      <i className="ri-map-pin-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">公司地址</p>
                      <p className="text-sm font-medium text-foreground-950">北京市朝阳区望京SOHO T2</p>
                    </div>
                  </div>
                </div>

                <h3 className="text-base font-heading font-bold text-foreground-950 mb-4">在线留言</h3>
                {(formError || formSuccess) && (
                  <div
                    className={`mb-4 p-3 rounded-lg text-sm ${
                      formError
                        ? 'bg-primary-50 text-primary-700 border border-primary-200'
                        : 'bg-accent-50 text-accent-700 border border-accent-200'
                    }`}
                  >
                    {formError || formSuccess}
                  </div>
                )}
                <form onSubmit={handleSubmit} data-readdy-form noValidate>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                        姓名 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 border border-background-300 rounded-lg text-sm text-foreground-950 bg-background-50 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                        邮箱 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 border border-background-300 rounded-lg text-sm text-foreground-950 bg-background-50 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                      留言内容 <span className="text-primary-500">*</span>
                    </label>
                    <textarea
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-2.5 border border-background-300 rounded-lg text-sm text-foreground-950 bg-background-50 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                      placeholder="请输入您的留言内容（最多500字）"
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                  </div>
                  <div className="honeypot-wrap">
                    <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" readOnly aria-hidden="true" />
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {submitting ? '提交中...' : '提交留言'}
                  </button>
                </form>
              </div>
            </section>
          </div>
        </div>

        <Footer />
        <ScrollToTop />
      </main>
    </>
  );
}